/**
 * A table's column arrangement as one URL-safe string — the pure half of `useColumnPrefs`.
 *
 * The arrangement is *which* columns a table shows and *in what order*, encoded as a single
 * search param: `?cols=email,organization,status,-delivered`. List order is column order; a
 * leading `-` means hidden. Keeping it in the URL rather than in state or storage is what makes
 * a configured table a link someone can paste to a colleague, and what makes F5 harmless.
 *
 * Everything here is a pure function over `{ order, hidden }`, so the rules that matter — an
 * unknown id can't blank the table, a column added next quarter can't break a link shared today —
 * are testable without a router, a DOM, or a component harness. See `columnPrefs-helper.test.ts`.
 *
 * Two invariants hold for every value returned from this module:
 *   • `order` contains each known id exactly once,
 *   • `hidden` is a subset of `order`, in `order`'s sequence.
 */

/** Which columns a table shows and in what order. `hidden` is always a subset of `order`. */
export interface ColumnPrefs<T extends string> {
  order: T[];
  hidden: T[];
}

const HIDDEN_PREFIX = "-";
const SEPARATOR = ",";

/**
 * The reference sequence of every configurable id: the defaults first (so appended columns keep
 * their intended neighbours), then anything the caller knows about but forgot to list.
 */
function canonicalOrder<T extends string>(allIds: readonly T[], defaultOrder: readonly T[]): T[] {
  const known = new Set<T>(allIds);
  const placed = new Set<T>();
  const result: T[] = [];
  for (const id of [...defaultOrder, ...allIds]) {
    if (!known.has(id) || placed.has(id)) continue;
    placed.add(id);
    result.push(id);
  }
  return result;
}

/** Rebuilds prefs so `hidden` is always a subset of `order`, in `order`'s sequence. */
function buildPrefs<T extends string>(order: readonly T[], hidden: ReadonlySet<T>): ColumnPrefs<T> {
  return { order: [...order], hidden: order.filter((id) => hidden.has(id)) };
}

/** The arrangement as one param value: `"email,organization,-delivered"`. */
function encode<T extends string>(prefs: ColumnPrefs<T>): string {
  const hidden = new Set(prefs.hidden);
  return prefs.order.map((id) => (hidden.has(id) ? `${HIDDEN_PREFIX}${id}` : id)).join(SEPARATOR);
}

/**
 * Reads the param back into an arrangement, tolerating every URL a user can hand-edit or a
 * colleague can send from an older build:
 *
 *   • `null` (no param at all) → the defaults.
 *   • ids the table no longer has → **discarded**; a renamed column must never blank the table.
 *   • ids the param never mentions → **appended at the end** with their default visibility, so a
 *     column added after a link was shared shows up instead of disappearing.
 *   • a param present but naming nothing (`?cols=`) → every configurable column hidden. That is an
 *     explicit "show nothing", which is why it is not treated as "no preference".
 */
export function parseCols<T extends string>(
  raw: string | null,
  allIds: readonly T[],
  defaults: ColumnPrefs<T>,
): ColumnPrefs<T> {
  const canonical = canonicalOrder(allIds, defaults.order);
  const defaultHidden = new Set(defaults.hidden);

  if (raw === null) return buildPrefs(canonical, defaultHidden);

  const tokens = raw
    .split(SEPARATOR)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
  if (tokens.length === 0) return buildPrefs(canonical, new Set(canonical));

  const known: ReadonlySet<string> = new Set<string>(allIds);
  const isKnown = (value: string): value is T => known.has(value);

  const order: T[] = [];
  const hidden = new Set<T>();
  for (const token of tokens) {
    const isHidden = token.startsWith(HIDDEN_PREFIX);
    const id = isHidden ? token.slice(HIDDEN_PREFIX.length) : token;
    if (!isKnown(id) || order.includes(id)) continue;
    order.push(id);
    if (isHidden) hidden.add(id);
  }
  for (const id of canonical) {
    if (order.includes(id)) continue;
    order.push(id);
    if (defaultHidden.has(id)) hidden.add(id);
  }

  return buildPrefs(order, hidden);
}

/**
 * The inverse of `parseCols` — with one deliberate asymmetry: the default arrangement serializes
 * to `null`, not to its own string. The caller deletes the param on `null`, so an untouched (or
 * reset) table has a clean URL and the defaults stay free to change later without stale links
 * pinning them.
 *
 * `defaults` is canonicalized against `prefs.order` before the comparison, the same way
 * `parseCols` canonicalizes it against `allIds`. Every value this module produces already
 * contains each known id exactly once (see the module doc above), so `prefs.order` is always a
 * valid stand-in for `allIds` here — using it keeps this function and `parseCols` agreeing on
 * what "the default" is without threading `allIds` through a second parameter. Comparing against
 * the raw, uncanonicalized `defaults.order` was the bug: whenever it omitted an id `parseCols`
 * had to append, the two encodings could never match and the param could never clear.
 */
export function serializeCols<T extends string>(
  prefs: ColumnPrefs<T>,
  defaults: ColumnPrefs<T>,
): string | null {
  const canonicalDefaults = buildPrefs(
    canonicalOrder(prefs.order, defaults.order),
    new Set(defaults.hidden),
  );
  const encoded = encode(prefs);
  return encoded === encode(canonicalDefaults) ? null : encoded;
}

/** Moves the column at `from` to `to`. Out-of-range or no-op indices return `prefs` untouched. */
export function reorderPref<T extends string>(
  prefs: ColumnPrefs<T>,
  from: number,
  to: number,
): ColumnPrefs<T> {
  const { order } = prefs;
  const inRange = (index: number) => Number.isInteger(index) && index >= 0 && index < order.length;
  if (from === to || !inRange(from) || !inRange(to)) return prefs;

  const next = [...order];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return prefs;
  next.splice(to, 0, moved);
  return buildPrefs(next, new Set(prefs.hidden));
}

/**
 * Nudges one column by `delta` places (−1 = up, +1 = down). The ends are walls, not a
 * wrap-around: ↑ on the first column does nothing rather than sending it to the bottom.
 */
export function movePref<T extends string>(
  prefs: ColumnPrefs<T>,
  id: T,
  delta: number,
): ColumnPrefs<T> {
  const from = prefs.order.indexOf(id);
  if (from === -1) return prefs;
  return reorderPref(prefs, from, from + delta);
}

/** Flips one column between shown and hidden. An id the table doesn't have is ignored. */
export function togglePref<T extends string>(prefs: ColumnPrefs<T>, id: T): ColumnPrefs<T> {
  if (!prefs.order.includes(id)) return prefs;
  const hidden = new Set(prefs.hidden);
  if (hidden.has(id)) hidden.delete(id);
  else hidden.add(id);
  return buildPrefs(prefs.order, hidden);
}

/**
 * Projects an arrangement onto the caller's column definitions: the visible ones, in the user's
 * order. `columns` is a lookup keyed by id (e.g. a table's `ALL_COLUMNS`), so this stays agnostic
 * about what a "column" actually is.
 */
export function applyPrefs<T extends string, C>(columns: Record<T, C>, prefs: ColumnPrefs<T>): C[] {
  const hidden = new Set(prefs.hidden);
  const result: C[] = [];
  for (const id of prefs.order) {
    if (hidden.has(id)) continue;
    const column = columns[id];
    if (column === undefined) continue;
    result.push(column);
  }
  return result;
}
